import React, { ReactElement, memo, useMemo } from "react";

import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";
// constants
import { RBI_GUIDELINES_TOOLTIP_KEY } from "@payment-ui/constants/TurboPayment";
import { TEXTS, ACCESSIBILITY_TEXTS } from "@payment-ui/constants/CartFooter";

import { format, isEmpty } from "@swiggy-private/js-utils";

// test ids
import { CART_FOOTER_TEST_IDS } from "@swiggy-private/cart-test-ids";

// types
import { TurboStripData, TurboStripEventAction } from "@swiggy-private/core-ui";
import { ACCESSIBILITY_ROLES } from "@swiggy-private/core-types";

// components
import { TurboStrip, AriaHidden, ScreenReaderOnly } from "@swiggy-private/core-ui";
import TurboPaymentWidgetShimmer from "@payment-ui/components/turbo-payment-widget/components/turbo-payment-widget-shimmer/TurboPaymentWidgetShimmer";
import TurboPaymentWidgetShimmerV2 from "@payment-ui/components/turbo-payment-widget/components/turbo-payment-widget-shimmer/v2/TurboPaymentWidgetShimmer";
import { TurboTooltip } from "@payment-ui/widgets/turbo-tooltip";
import { getTurboTooltipViewFreqCappPayload } from "@payment-ui/components/turbo-payment-widget/constants";

//styles
import { ButtonWrapper, Footer, FooterFirst, LoaderWrapper, TurboStripContainer } from "./styled";
import { ProceedMode, useLocalStorageBasedFrequencyCapping } from "@swiggy-private/checkout-core";

export interface TurboCartFallbackFooterProps {
    cartTotal: string;
    viewDetailedBill?: VoidFunction;
    makePayment: () => void;
    turboStripData?: TurboStripData;
    onTurboStripClick?: TurboStripEventAction;
    onTurboStripImpression?: TurboStripEventAction;
    loading?: boolean;
    isTurboV2?: boolean;
    eventOnTooltipClose?: VoidFunction;
    eventOnImpressionTooltip?: (stripMsg?: string) => void;
    tooltipDetails?: {
        text?: string;
        enabled?: boolean;
        key?: string;
        icon?: string;
        maxViewFrequency?: number;
    };
}

function TurboCartFallbackFooter(props: TurboCartFallbackFooterProps): ReactElement {
    const {
        turboStripData,
        makePayment,
        onTurboStripClick,
        onTurboStripImpression,
        loading,
        isTurboV2,
        cartTotal,
        tooltipDetails,
        eventOnTooltipClose,
        eventOnImpressionTooltip,
    } = props;

    const { shouldProceed: shouldRenderCustomTooltip } = useLocalStorageBasedFrequencyCapping(
        getTurboTooltipViewFreqCappPayload(tooltipDetails?.key, tooltipDetails?.maxViewFrequency),
    );

    const showTurboTooltip = useMemo(
        () => tooltipDetails?.enabled && shouldRenderCustomTooltip({ mode: ProceedMode.CHECK_AND_UPDATE }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tooltipDetails?.enabled, shouldRenderCustomTooltip],
    );

    const onTooltipVisible = () => {
        eventOnImpressionTooltip?.(tooltipDetails?.text);
    };

    const onTooltipClose = () => {
        eventOnTooltipClose?.();
    };

    const accessibilityText = useMemo(() => {
        return `${format(ACCESSIBILITY_TEXTS.CART_TOTAL, String(cartTotal))}`;
    }, [cartTotal]);

    const formattedCartTotal = useMemo(() => {
        const total = Number(cartTotal);
        if (isNaN(total)) {
            return `₹0`;
        }
        return `₹${total.toFixed(0)}`;
    }, [cartTotal]);

    if (loading) {
        return (
            <LoaderWrapper>{isTurboV2 ? <TurboPaymentWidgetShimmerV2 /> : <TurboPaymentWidgetShimmer />}</LoaderWrapper>
        );
    }
    return (
        <>
            {showTurboTooltip && (
                <TurboTooltip
                    onTooltipVisible={onTooltipVisible}
                    onTooltipClose={onTooltipClose}
                    tooltipDetails={tooltipDetails}
                    bottomArrowPosition={isTurboV2 ? "left" : "right"}
                    size={tooltipDetails?.key === RBI_GUIDELINES_TOOLTIP_KEY ? "large" : "default"}
                />
            )}

            {!isEmpty(turboStripData) && (
                <TurboStripContainer>
                    <TurboStrip
                        isTurbo={false}
                        onClickTurboStrip={onTurboStripClick}
                        data={turboStripData}
                        onImpressionTurboStrip={onTurboStripImpression}
                    />
                </TurboStripContainer>
            )}

            <Footer role={ACCESSIBILITY_ROLES.GROUP} aria-label={ACCESSIBILITY_TEXTS.PAYMENT_GROUP}>
                <ScreenReaderOnly text={accessibilityText} />
                {!isEmpty(props.viewDetailedBill) && (
                    <FooterFirst onClick={props.viewDetailedBill}>
                        <AriaHidden as="div">
                            <Text
                                as="span"
                                variant="V2.subheading1.neutral.bold"
                                color="text_Highest_Emphasis"
                                data-testid={CART_FOOTER_TEST_IDS.BILL_TOTAL}
                            >
                                {formattedCartTotal}
                            </Text>
                        </AriaHidden>
                        <div data-testid={CART_FOOTER_TEST_IDS.VIEW_DETAILED_BILL_BTN}>
                            <Text as="span" variant="V2.body3.neutral.medium" color="background_Button_Primary">
                                {TEXTS.VIEW_DETAILED_BILL}
                            </Text>
                        </div>
                    </FooterFirst>
                )}
                <ButtonWrapper>
                    <SuperButton
                        variant="fullwidth"
                        buttonType="primary"
                        onClick={makePayment}
                        aria-label={ACCESSIBILITY_TEXTS.MAKE_PAYMENT}
                        role="button"
                        tabIndex={0}
                        data-testid={CART_FOOTER_TEST_IDS.MAKE_PAYMENT}
                        buttonSize="large"
                    >
                        {TEXTS.PROCEED_TO_PAY}
                    </SuperButton>
                </ButtonWrapper>
            </Footer>
        </>
    );
}

export default memo(TurboCartFallbackFooter);
